import React, { useEffect, useState } from "react";

import { deleteCookie } from "cookies-next";
import { FormattedMessage, FormattedDate } from "react-intl";
import { toast } from "sonner";

import { Divider } from "@/components/atoms/Divider";
import { Skeleton } from "@/components/atoms/Skeleton";
import { Text } from "@/components/atoms/Text";
import { Alert, AlertDescription } from "@/components/molecules/Alert";
import { Form } from "@/components/molecules/Form";
import { PaymentMethodMessagingElement } from "~/components/stripe";
import { useEnvData } from "~/context/EnvDataContext";
import { ListingData, useListingData } from "~/context/ListingPageDataContext";
import { useMessagesData } from "~/context/MessagesDataContext";
import { useIsKlarnaSupported } from "~/hooks/use-is-klarna-supported";
import { useTransactable } from "~/hooks/use-transactable";
import useRedirectToSigninSignup from "~/hooks/useRedirectToSigninSignup";
import { useProfileData } from "~/queries/profile/useProfile";
import { IFaqs } from "~/types/api-types";
import { createCookieOptions } from "~/utils/cookie-utils";

import { handleStartBookingAnalytics } from "../book-listing-analytics";
import { useBookListingPriceData } from "../book-listing-hooks";
import { useBookListingContext } from "../BookListingContext";
import { getFsvOverDurationState } from "../fsv-over-duration.utils";
import { useFsvDepositLayout } from "../hooks/use-fsv-deposit-layout";
import { useZeroDownPayment } from "../hooks/use-zero-down-payment";
import { IdvWarning } from "../IdvWarning";

import { AttendeeSelectField } from "./AttendeeSelectField";
import { BookingScheduleInput } from "./BookingScheduleInput";
import { BookListingPriceSummary } from "./BookListingPriceSummary/BookListingPriceSummary";
import { BookListingSubmitButton } from "./BookListingSubmitButton";
import { FsvPaymentScheduleSection } from "./FsvPaymentSchedule";
import { MinHoursWarning } from "./MinHoursWarning";

interface BookListingFormProps {
  faqs?: IFaqs;
  guestNeedsVerification: boolean | undefined;
  pageTitle: string;
}

export const BookListingForm = ({
  faqs,
  guestNeedsVerification,
  pageTitle,
}: BookListingFormProps) => {
  const { data: listingData = {} as ListingData } = useListingData();
  const profile = useProfileData();
  const messages = useMessagesData();
  const env = useEnvData();
  const { checkTransactable } = useTransactable();
  const { showZeroDownSkeleton, isZeroDownPayment } = useZeroDownPayment();
  const { depositBreakdown, showDepositLayout } = useFsvDepositLayout();

  const [showMinHoursWarning, setShowMinHoursWarning] = useState(false);

  const { quote, bookListingForm, isFetchingQuote } = useBookListingContext();
  const { booking } = quote?.spaceRentalContract?.meta || {};

  const { exceedsMinDuration: exceedsMinFSV, minBookingDurationHours } =
    getFsvOverDurationState({
      isFullServiceVenue: listingData.fullService?.active,
      minBookingDurationHours:
        listingData.fullService?.min_booking_duration_hours,
      selectedDurationHours: booking?.duration,
    });

  useEffect(() => {
    const cookieOptions = createCookieOptions({
      expires: new Date(0),
    });

    deleteCookie("booking_attendee_tier", cookieOptions);
  }, []);

  useEffect(() => {
    if (!booking) {
      setShowMinHoursWarning(false);
      return;
    }

    const isMinimumDuration =
      (booking?.duration || 0) >=
      parseFloat(listingData.listingMinimumDuration ?? "0");

    if (!isMinimumDuration && !listingData.isInstantBookActive) {
      setShowMinHoursWarning(true);
      window.analyticsIntegrations.showMinHoursWarning(isMinimumDuration);
      return;
    }
    setShowMinHoursWarning(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [booking?.duration]);

  const { handleRedirectToSignupPage, isUserLoggedIn } =
    useRedirectToSigninSignup();

  const onBookClick = () => {
    if (!booking) {
      toast.error(
        <FormattedMessage defaultMessage="Please select a date and time to book." />,
      );
      return;
    }

    handleStartBookingAnalytics({
      rateDetails: booking?.rate_details,
      listingData,
      faqs,
      profile,
      pageTitle,
      userMessagesCount: messages?.threadCount,
    });

    const searchParams = new URLSearchParams(window.location.search);
    const requestParam = searchParams.get("request");
    const url = `/bkc/${listingData.listingId}${
      requestParam ? `?request=${requestParam}` : ""
    }`;

    if (isUserLoggedIn) {
      checkTransactable(() => {
        window.location.href = url;
      });
    } else {
      handleRedirectToSignupPage("fee", url);
    }
  };

  const isKlarnaSupported = useIsKlarnaSupported();
  const { total, currency, country } = useBookListingPriceData();
  const dtsFullRefund = booking?.cancellation_policy?.dts_full_refund;

  return (
    <Form {...bookListingForm} spacing={24}>
      <BookingScheduleInput />

      {exceedsMinFSV && (
        <Alert appearance="purple" isDismissable={false}>
          <AlertDescription className="tw-mb-0">
            <Text typeface="body-1-m">
              <FormattedMessage
                defaultMessage="Reservations over {duration, plural, one {# hour} other {# hours}} may require a pricing update from the venue."
                values={{
                  duration: minBookingDurationHours,
                }}
              />
            </Text>
          </AlertDescription>
        </Alert>
      )}

      <div className="tw-border-t tw-border-gray-200 tw-mx-[-24px]" />

      {!listingData.fullService?.active &&
        listingData.listingAttendeePricing?.tiers?.length > 1 && (
          <AttendeeSelectField
            listingAttendeePricing={listingData.listingAttendeePricing}
            currency={listingData.listingCurrency}
          />
        )}

      {(guestNeedsVerification || showMinHoursWarning) && (
        <>
          <Divider spacing={24} />
          {guestNeedsVerification && (
            <IdvWarning
              zendeskUrlLocale={env.currentLocaleData.data.zendeskUrlLocale}
            />
          )}

          {guestNeedsVerification && showMinHoursWarning && (
            <Divider spacing={24} />
          )}

          {showMinHoursWarning && (
            <MinHoursWarning
              minimumDuration={listingData.listingMinimumDuration}
            />
          )}
          <Divider spacing={24} />
        </>
      )}

      <BookListingPriceSummary
        title={<FormattedMessage defaultMessage="Price details" />}
      />

      {bookListingForm.formState.errors?.root?.message && (
        <Text className="tw-text-red-700 tw-body-1">
          {(bookListingForm.formState.errors.root.message as string)
            .split("\n")
            .map((line, i, arr) => (
              <React.Fragment key={i}>
                {line}
                {i < arr.length - 1 && <br />}
              </React.Fragment>
            ))}
        </Text>
      )}

      {isFetchingQuote ? (
        /* Single skeleton when loading: zero-down experiment takes precedence over Klarna */
        showZeroDownSkeleton ? (
          <div className="tw-text-center tw-text-gray-600">
            <Skeleton height={20} width={140} classNames="tw-mx-auto" />
          </div>
        ) : (
          isKlarnaSupported && (
            <div className="tw-flex tw-justify-center">
              <Skeleton height={24} width={280} />
            </div>
          )
        )
      ) : (
        <>
          {isZeroDownPayment && (
            <div className="tw-flex tw-flex-col tw-items-center tw-text-center">
              <Text typeface="body-1-m" className="tw-text-gray-900 tw-mb-4">
                <FormattedMessage defaultMessage="$0 due today" />
              </Text>
              {dtsFullRefund && (
                <Text typeface="body-1" className="tw-text-gray-600">
                  <FormattedMessage
                    defaultMessage="Free cancellation before {date}"
                    values={{
                      date: (
                        <FormattedDate
                          value={dtsFullRefund}
                          month="short"
                          day="numeric"
                        />
                      ),
                    }}
                  />
                </Text>
              )}
            </div>
          )}

          {isKlarnaSupported && !isZeroDownPayment && (
            <PaymentMethodMessagingElement
              amount={total}
              currency={currency}
              country={country}
              id="payment-method-messaging-element-desktop"
            />
          )}
        </>
      )}

      {showDepositLayout && depositBreakdown && (
        <>
          <div className="tw-border-t tw-border-gray-200 tw-mx-[-24px]" />
          <FsvPaymentScheduleSection headingId="fsv-payment-schedule-title-desktop" />
        </>
      )}

      <div className="tw-border-t tw-border-gray-200 tw-mx-[-24px]" />

      <BookListingSubmitButton
        quote={quote}
        booking={booking}
        listingData={listingData}
        onClick={onBookClick}
      />
    </Form>
  );
};
